import { useUserVenueSession } from "@shared/modules/venue/hooks/useUserVenueSession";
import { useModelService } from "@shared/services/api/hooks/useModelService";
import { taskLoader } from "@shared/services/loaders/task-loader";
import {
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "react-query";
import { AxiosResponse } from "axios";
import VenueBasedEventRepository from "../../repositories/VenueBasedEventRepository";
import type {
  RatingSummaryType,
  VenueBasedEventSerializer,
} from "../../types/events";

const useVenueEventsService = (venueID?: number) => {
  const { data: currentVenue } = useUserVenueSession();
  const currentVenueId = currentVenue?.id;
  const venueId = venueID || currentVenueId;
  const repository = new VenueBasedEventRepository({ venueId });
  const queryClient = useQueryClient();

  const modelService = useModelService(repository);

  const useAsyncPartialUpdate = ({
    options,
  }: {
    options?: UseQueryOptions;
  }) => {
    return modelService.usePartialUpdate({
      options: {
        onSuccess: async (response?: { job_id?: string }) => {
          if (response?.job_id) {
            const result = await taskLoader(response.job_id);
            await queryClient.invalidateQueries(repository.getRepositoryName());
            options?.onSuccess?.(result);
          } else {
            options?.onSuccess?.(response);
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      },
      ...options,
    });
  };

  const useBulkUpdate = ({
    id,
    options,
  }: {
    id: number;
    options?: UseQueryOptions;
  }) => {
    return modelService.useGenericMutation("bulkUpdate", "POST", {
      injects: { id },
      options: {
        onSuccess: async (result: { data: { job_id: string } }) => {
          const res = await taskLoader(result.data.job_id);
          await queryClient.invalidateQueries(repository.getRepositoryName());

          options?.onSuccess?.(res);
        },
        onError: (result) => {
          options?.onError?.(result);
        },
      },
    });
  };

  const useGenerateRatingSummary = ({
    id,
    options,
  }: {
    id: string | number;
    options?: UseMutationOptions<
      AxiosResponse<{ rating_summary: RatingSummaryType }>,
      unknown,
      Record<string, never>
    >;
  }) => {
    return modelService.useGenericMutation<
      { rating_summary: RatingSummaryType },
      Record<string, never>
    >("generateRatingSummary", "POST", {
      injects: { id },
      options: {
        onSuccess: async (result) => {
          await queryClient.invalidateQueries([
            repository.getRepositoryName(),
            id,
          ]);
          options?.onSuccess?.(result, {}, undefined);
        },
        onError: (error) => {
          options?.onError?.(error, {}, undefined);
        },
      },
    });
  };

  const setRetrieveData = (
    id: string,
    updater: (
      prev: VenueBasedEventSerializer | undefined
    ) => VenueBasedEventSerializer | undefined
  ): void => {
    const key = modelService.queryKeyManager.generateKey(["retrieve", id]);
    queryClient.setQueryData(key, (prev: unknown) =>
      updater(prev as VenueBasedEventSerializer | undefined)
    );
  };

  return {
    ...modelService,
    useAsyncPartialUpdate,
    useBulkUpdate,
    useGenerateRatingSummary,
    setRetrieveData,
  };
};

export default useVenueEventsService;

export type UseVenueEventsService = ReturnType<typeof useVenueEventsService>;
